<script setup lang="ts">
/**
 * Wave 1 — Property access modals (Agent C).
 *
 * Guest gate fired when an unauthenticated visitor tries to download
 * a project document (brochure, floor plan, payment plan, etc.).
 * Renders a small contextual row with the document name + meta.
 */

const { t } = useI18n();

const {
  documentName,
  documentMeta = 'PDF',
  projectInitials = 'Insiders',
} = defineProps<{
  documentName?: string;
  documentMeta?: string;
  /** 2-letter abbreviation drawn into the document thumb (e.g. SA for Sobha Aurora). */
  projectInitials?: string;
}>();

const documentNameText = computed(() => documentName ?? t('property.access.modalGuestDocument.defaultDocumentName'));

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'signup-click'): void;
  (e: 'signin-click'): void;
}>();

const titleId = useId();
</script>

<template>
  <ModalShell
    accent="gold"
    :labelled-by="titleId"
    @close="emit('close')"
  >
    <ModalIcon
      kind="doc"
      accent="gold"
    />

    <div class="mb-5 flex items-center gap-3 rounded-xl border border-black-10 bg-black-03 p-3">
      <div class="h-14 w-[42px] shrink-0 overflow-hidden rounded-md">
        <svg
          viewBox="0 0 60 80"
          preserveAspectRatio="xMidYMid meet"
          class="h-full w-full"
        >
          <rect
            width="60"
            height="80"
            rx="4"
            fill="#FFB648"
          />
          <rect
            x="8"
            y="50"
            width="44"
            height="3"
            fill="white"
            opacity="0.9"
          />
          <rect
            x="8"
            y="58"
            width="36"
            height="3"
            fill="white"
            opacity="0.7"
          />
          <rect
            x="8"
            y="66"
            width="40"
            height="3"
            fill="white"
            opacity="0.5"
          />
          <text
            x="30"
            y="32"
            text-anchor="middle"
            font-size="14"
            font-weight="700"
            fill="white"
            font-family="Fraunces, serif"
          >
            {{ projectInitials }}
          </text>
        </svg>
      </div>
      <div class="min-w-0 flex-1">
        <div class="font-mono text-[9.5px] font-bold uppercase tracking-[0.1em] text-gold-primary-120">
          {{ t('property.access.modalGuestDocument.tryingToDownload') }}
        </div>
        <div class="truncate text-[13.5px] font-semibold text-black-100">{{ documentNameText }}</div>
        <div class="text-[11.5px] text-black-60">{{ documentMeta }}</div>
      </div>
    </div>

    <h2
      :id="titleId"
      class="mb-3 font-serif text-[24px] font-semibold leading-[1.15] tracking-[-0.015em] text-black-100"
    >
      {{ t('property.access.modalGuestDocument.heading') }}
    </h2>
    <p class="mb-5 text-[13.5px] leading-[1.55] text-black-70">
      {{ t('property.access.modalGuestDocument.body') }}
    </p>
    <ul class="mb-6 space-y-2">
      <CheckBullet accent="gold">{{ t('property.access.modalGuestDocument.bullet1') }}</CheckBullet>
      <CheckBullet accent="gold">{{ t('property.access.modalGuestDocument.bullet2') }}</CheckBullet>
      <CheckBullet accent="gold">{{ t('property.access.modalGuestDocument.bullet3') }}</CheckBullet>
      <CheckBullet accent="gold">{{ t('property.access.modalGuestDocument.bullet4') }}</CheckBullet>
    </ul>
    <div class="flex flex-col gap-2">
      <button
        type="button"
        class="inline-flex h-11 items-center justify-center rounded-[10px] bg-black-100 px-5 text-[14px] font-bold text-white transition-colors hover:bg-black-90"
        @click="emit('signup-click')"
      >
        <span
          >{{ t('property.access.modalGuestDocument.signUpToDownload') }}
          <span
            class="inline-block rtl:-scale-x-100"
            aria-hidden="true"
            >→</span
          ></span
        >
      </button>
      <button
        type="button"
        class="inline-flex h-10 items-center justify-center rounded-[10px] text-[13px] font-semibold text-black-80 transition-colors hover:bg-black-05"
        @click="emit('signin-click')"
      >
        {{ t('property.access.modalGuestDocument.haveAccount') }}
      </button>
    </div>
    <div class="mt-4 text-center text-[11.5px] text-black-60">
      {{ t('property.access.modalGuestDocument.footNote') }}
    </div>
  </ModalShell>
</template>
